import { Button, Dropdown, Menu, message } from "antd";
import Icon from "../../../assets/icons/icon";
import LeasingStore from "../../../contexts/leasingStore";
import { LeasingAdminType, LeasingCaseType } from "../../../types/leasingTypes";
import format from "../../../utilities/formatNumbers";
import { MailOutlined } from "@ant-design/icons"
import moment from 'moment'
import { getLeasingExpirationDate } from "./admin/leasingAdminMotorAgency";

type EmailType = {
  email: string;
  subject: string;
  body: string;
}

type LeasingStandardMail = {
  key: string;
  title: string;
  createEmail: (leasingCase: LeasingCaseType, adminValues: LeasingAdminType | null) => EmailType;
}

type LeasingStandardMailGroup = {
  title: string;
  mails: LeasingStandardMail[];
}

const maxMailtoLength = 1800;

export default function LeasingStandardMailsDropdown() {
  const { leasingCase, adminValues } = LeasingStore.useContainer()

  const openStandardMail = async (standardMail: LeasingStandardMail) => {
    const email = standardMail.createEmail(leasingCase, adminValues);
    const mailto = createMailtoLink(email);

    if (mailto.length > maxMailtoLength) {
      await navigator.clipboard.writeText(normalizeEmailBody(email.body));
      message.success("Brødtekst kopieret til udklipsholder");
      window.open(createMailtoLink({ ...email, body: "Klik Ctrl-V. \n Emailen var for lang, så den blev kopieret til din udklipsholder istedet." }), "_self");
      return;
    }

    window.open(mailto, "_self");
  }

  const menu = (
    <Menu>
      {standardMailGroups.map((group) => (
        <Menu.ItemGroup
          key={group.title}
          title={
            <div style={{ marginLeft: -12, marginRight: -12, padding: "5px 12px", background: "#EFF3F5" }}>
              {group.title}
            </div>
          }
        >
          {group.mails.map((standardMail) => (
            <Menu.Item
              key={standardMail.key}
              icon={<Icon name="mail-outline" />}
              onClick={() => openStandardMail(standardMail)}
            >
              {standardMail.title}
            </Menu.Item>
          ))}
        </Menu.ItemGroup>
      ))}
    </Menu>
  )

  return (
    <Dropdown overlay={menu}>
      <Button>
        Mails
        <MailOutlined style={{ fontSize: 14, verticalAlign: "middle" }} />
      </Button>
    </Dropdown>
  )
}

const standardMailGroups: LeasingStandardMailGroup[] = [
  {
    title: "Validering",
    mails: [
      {
        key: "credit_validation",
        title: "Kreditvalidering",
        createEmail: (leasingCase) => ({
          email: getCustomerEmail(leasingCase),
          subject: `${getCaseId(leasingCase)} - ${getMakeAndModel(leasingCase)} - Kreditvalidering`,
          body: `Kære ${getCustomerFirstName(leasingCase)},

Vi er nu nået til kreditvalideringen af din leasingaftale. For at vi kan behandle sagen videre, har vi brug for nogle oplysninger fra dig.

Til brug for valideringen skal du:
• Udfylde den tilsendte formular fra vores samarbejdspartner, Uniify.
• Give os adgang til din skattemappe. Se eventuelt vejledningen nedenfor: https://scribehow.com/shared/Web-guide_Sadan_giver_du_adgang_til_dine_skatteoplysninger__MA0xXFxzSru7IsS0OpB6mw

VÆR OPMÆRKSOM!
Der er to steder, hvor der står "Personlige skatteoplysninger". Det er den nederste under "TastSelv Borger", der skal markeres.

Når du har givet adgang til skattemappen, bedes du bekræfte det ved at besvare denne mail.

Rigtig god dag.`
        })
      },
      {
        key: "credit_validation_vip",
        title: "Kreditvalidering VIP",
        createEmail: (leasingCase) => ({
          email: getCustomerEmail(leasingCase),
          subject: `${getCaseId(leasingCase)} - ${getMakeAndModel(leasingCase)} - Kreditvalidering VIP`,
          body: `Kære ${getCustomerFirstName(leasingCase)},

Som en del af valideringen af din leasingaftale har vi brug for enkelte oplysninger fra dig.

Du modtager derfor en VIP-formular fra vores samarbejdspartner, Uniify, som du bedes udfylde.
Når formularen er udfyldt, kan vi behandle din sag videre.

Rigtig god dag. 😊`
        })
      },
      {
        key: "rejection_customer",
        title: "Afvisning kunde",
        createEmail: (leasingCase) => ({
          email: getCustomerEmail(leasingCase),
          subject: `${getCaseId(leasingCase)} - ${getMakeAndModel(leasingCase)} - Afvisning`,
          body: `Kære ${getCustomerFirstName(leasingCase)},

Vi har nu færdigbehandlet behandlingen af din ansøgning.

På baggrund af en samlet kreditmæssig vurdering kan vi desværre ikke tilbyde dig den ønskede leasingaftale.

Vurderingen er foretaget på baggrund af de oplysninger og den dokumentation, der indgår i sagen, samt vores gældende kreditpolitik og interne vurderingskriterier.
Vores kreditpolitik og vurderingskriterier er fortrolige, og vi har derfor ikke mulighed for at uddybe eller kommentere den konkrete begrundelse for afgørelsen.

Vi beklager, at vi ikke kan imødekomme din ansøgning.`
        })
      },
      {
        key: "rejection_dealer",
        title: "Afvisning forhandler",
        createEmail: (leasingCase) => ({
          email: leasingCase.client?.email ?? "",
          subject: `${getCaseId(leasingCase)} - ${getMakeAndModel(leasingCase)} - Afvisning`,
          body: `Kære ${leasingCase.client?.name || "[dealer_salesperson_name]"},

Vi har nu færdigbehandlet leasingansøgningen vedrørende din kunde.

På baggrund af en samlet kreditmæssig vurdering kan vi desværre ikke tilbyde kunden den ønskede leasingaftale.

Vurderingen er foretaget på baggrund af de oplysninger og den dokumentation, der indgår i sagen, samt vores gældende kreditpolitik og interne vurderingskriterier.
Vores kreditpolitik og vurderingskriterier er fortrolige, og vi har derfor ikke mulighed for at uddybe eller kommentere den konkrete begrundelse for afgørelsen.

Du bedes videreformidle afslaget til kunden.`
        })
      }
    ]
  },
  {
    title: "Før aktivering",
    mails: [
      {
        key: "documentation",
        title: "Dokumentation",
        createEmail: (leasingCase) => ({
          email: "dok@danskmotorfinans.dk",
          subject: `Dokumentation - ${getLeasingCaseUrl(leasingCase)}`,
          body: `Hej TAX,

Vil I lave dokumentation på denne?

Det er en ny leasingaftale.

Stelnummer: ${getVin(leasingCase)}
Kilometerstand: ${getMileage(leasingCase)}

${getLeasingCaseUrl(leasingCase)}

På forhånd tak 😊`
        })
      },
      {
        key: "leasing_code",
        title: "Leasingkode",
        createEmail: (leasingCase, adminValues) => ({
          email: "",
          subject: `${getCaseId(leasingCase)} - ${getMakeAndModel(leasingCase)} - Leasingkode`,
          body: `Kære,

Vi har nu oprettet leasingkoden til køretøjet.

Leasingkoden er gyldig fra den ${getStartDate(leasingCase, adminValues)}, da den følger leasingaftalens startdato.

Til brug for indregistrering:
• Leasingkode: ${leasingCase.leasingCode ?? ""}
• Forsikring: ${getInsuranceCompanyName(leasingCase)}

Når bilen er indregistreret, bedes du sende følgende retur til os:
• Kvittering for indregistrering af køretøjet
• 2 billeder af bilen - henholdsvis forfra og bagfra - hvor nummerpladerne er tydeligt monteret og synlige
• 1 billede af bilens stelnummer

Rigtig god dag. 😊`
        })
      },
      {
        key: "payment_reminder",
        title: "Påmindelse betaling",
        createEmail: (leasingCase, adminValues) => ({
          email: getCustomerEmail(leasingCase),
          subject: `${getCaseId(leasingCase)} - ${getMakeAndModel(leasingCase)} - Påmindelse om betaling`,
          body: `Kære ${getCustomerFirstName(leasingCase)},

Vi kan endnu ikke se, at vi har modtaget betalingen vedrørende din nye leasingaftale nr. ${getCaseId(leasingCase)}.

For at vi kan aktivere leasingaftalen som planlagt den ${getStartDate(leasingCase, adminValues)}, er det vigtigt, at betalingen er modtaget inden da.

Jeg har vedhæftet betalingsadviseringen igen for en god ordens skyld.
Hvis du allerede har foretaget betalingen, kan du naturligvis se bort fra denne mail.

Har du spørgsmål, er du meget velkommen til at kontakte os.`
        })
      },
      {
        key: "inspection_reminder",
        title: "Påmindelse syn",
        createEmail: (leasingCase, adminValues) => ({
          email: getCustomerEmail(leasingCase),
          subject: `${getCaseId(leasingCase)} - ${getMakeAndModel(leasingCase)} - Vigtigt vedrørende syn før fornyelse`,
          body: `Kære ${getCustomerFirstName(leasingCase)},

Det er snart tid til fornyelse af din leasingaftale, og vi kan se, at køretøjet samtidig er indkaldt til syn.

Det er meget vigtigt, at køretøjet bliver synet og godkendt senest den ${getStartDate(leasingCase, adminValues)}. Hvis køretøjet ikke er godkendt ved syn inden denne dato, kan det ikke registreres i DMR, og vi kan derfor ikke registrere den nye leasingaftale.
Det kan medføre, at den nuværende leasingaftale overskrides, hvilket i værste fald kan medføre fuld registreringsafgift eller betyde, at køretøjets nummerplader skal afmeldes.

Vi beder dig derfor sørge for, at synet bliver gennemført og køretøjet godkendt senest den ${getStartDate(leasingCase, adminValues)}.

Har du spørgsmål, eller er der noget, vi kan hjælpe med i forbindelse med synet eller fornyelsen, er du meget velkommen til at kontakte os.`
        })
      }
    ]
  },
  {
    title: "Aktivering",
    mails: [
      {
        key: "welcome",
        title: "Velkomstmail",
        createEmail: (leasingCase, adminValues) => createWelcomeEmail(leasingCase, adminValues, false)
      },
      {
        key: "welcome_releasing",
        title: "Velkomstmail releasing",
        createEmail: (leasingCase, adminValues) => createWelcomeEmail(leasingCase, adminValues, true)
      }
    ]
  },
  {
    title: "Afslutning",
    mails: [
      {
        key: "closed_with_deposit",
        title: "Afslutning med depositum",
        createEmail: (leasingCase) => createClosedEmail(leasingCase, true)
      },
      {
        key: "closed_without_deposit",
        title: "Afslutning uden depositum",
        createEmail: (leasingCase) => createClosedEmail(leasingCase, false)
      }
    ]
  }
]

function createWelcomeEmail(leasingCase: LeasingCaseType, adminValues: LeasingAdminType | null, isReleasing: boolean): EmailType {
  return {
    email: getCustomerRecipients(leasingCase),
    subject: `${getCaseId(leasingCase)} - ${getMakeAndModel(leasingCase)} - Tillykke med din nye leasingaftale`,
    body: `Kære ${getCustomerGreetingName(leasingCase)},

Tillykke med den nye leasingaftale! 😊

Leasingaftalen med aftalenummer ${getCaseId(leasingCase)} er nu aktiv og løber i perioden:
${getStartDate(leasingCase, adminValues)} - ${getEndDate(leasingCase, adminValues)}${isReleasing ? "\nDette er samtidig en bekræftelse på, at din tidligere leasingaftale er afsluttet." : ""}
${getBusinessInfoBlock(leasingCase)}${getInsuranceBlock(leasingCase)}
Din leasingaftale og midlertidige registreringsattest er vedhæftet denne mail.
Har du spørgsmål til aftalen eller andet, er du naturligvis altid velkommen til at kontakte os.

Vi vil sætte stor pris på, hvis du vil bruge et øjeblik på at dele din oplevelse med os på Trustpilot her https://dk.trustpilot.com/evaluate/danskmotorfinans.dk.

Vi ønsker dig rigtig god tur!`
  }
}

function createClosedEmail(leasingCase: LeasingCaseType, hasDeposit: boolean): EmailType {
  return {
    email: getCustomerEmail(leasingCase),
    subject: `${getCaseId(leasingCase)} - ${getMakeAndModel(leasingCase)} - Afsluttet leasingaftale`,
    body: `Kære ${getCustomerFirstName(leasingCase)},

Din leasingaftale med aftalenummer ${getCaseId(leasingCase)} er nu afsluttet.

${hasDeposit
        ? "I forbindelse med tilbagebetaling af dit depositum bedes du fremsende registrerings- og kontonummer på den konto, som beløbet skal overføres til. Depositummet udbetales, så snart sagen er afsluttet i vores bogholderi."
        : "Der er ikke yderligere, du skal foretage dig i forbindelse med aftalen. \nHar du spørgsmål til afslutningen, er du naturligvis altid velkommen til at kontakte os."
      }

Tak for denne gang - og på gensyn. 😊`
  }
}

function createMailtoLink(email: EmailType) {
  const subject = encodeURIComponent(email.subject);
  const body = email.body ? `&body=${encodeURIComponent(normalizeEmailBody(email.body))}` : "";
  return `mailto:${encodeURIComponent(email.email)}?subject=${subject}${body}`;
}

function normalizeEmailBody(body: string) {
  return body
    .trimStart()
    .replace(/\r\n|\r|\n/g, "\n")
    .replace(/\n/g, "\r\n");
}

function getCaseId(leasingCase: LeasingCaseType) {
  return String(leasingCase.id ?? "");
}

function getMakeAndModel(leasingCase: LeasingCaseType) {
  return [leasingCase.vehicle?.brand, leasingCase.vehicle?.model].filter(Boolean).join(" ");
}

function getCustomerEmail(leasingCase: LeasingCaseType) {
  return leasingCase.customer?.email ?? "";
}

function getContactPersonEmail(leasingCase: LeasingCaseType) {
  return leasingCase.contactPerson?.email ?? "";
}

function getCustomerRecipients(leasingCase: LeasingCaseType) {
  if (leasingCase.customer?.typeId === 2) {
    return [getCustomerEmail(leasingCase), getContactPersonEmail(leasingCase)].filter(Boolean).join(";");
  }
  return getCustomerEmail(leasingCase);
}

function getCustomerFirstName(leasingCase: LeasingCaseType) {
  return leasingCase.customer?.nameFirst || leasingCase.customer?.name?.split(" ")?.[0] || "";
}

function getCustomerGreetingName(leasingCase: LeasingCaseType) {
  if (leasingCase.customer?.typeId === 2) return leasingCase.customer?.name ?? "";
  return getCustomerFirstName(leasingCase);
}

function getStartDate(leasingCase: LeasingCaseType, adminValues: LeasingAdminType | null) {
  const date = adminValues?.deal?.activeSettings.startDateDmr || leasingCase.contract?.agreements?.expectedStartDate || leasingCase.deal?.input?.leasingDates?.[0];

  return date ? format.date(date) : "";
}

function getEndDate(leasingCase: LeasingCaseType, adminValues: LeasingAdminType | null) {
  let date: null | string | undefined = leasingCase?.deal?.input?.leasingDates?.[1];
  if (adminValues?.deal?.activeSettings.startDateDmr && leasingCase?.deal?.input?.leasingPeriod) {
    date = getLeasingExpirationDate(leasingCase, adminValues)
  }
  return date ? format.date(date) : "";
}

function getVin(leasingCase: LeasingCaseType) {
  return leasingCase.vehicle?.vin ?? "";
}

function getMileage(leasingCase: LeasingCaseType) {
  return leasingCase.vehicle?.mileage ? format.number(leasingCase.vehicle.mileage) : "";
}

function getInsuranceCompanyName(leasingCase: LeasingCaseType) {
  return leasingCase.contract?.insuranceCompany?.name ?? "";
}

function getMonthlyVatDeduction(leasingCase: LeasingCaseType) {
  return format.price(leasingCase.deal?.output?.monthlyPayment?.vatDeduction ?? 0);
}

function getTaxation(leasingCase: LeasingCaseType) {
  return format.price(leasingCase.deal?.output?.monthlyPayment?.taxation ?? 0);
}

function getLeasingCaseUrl(leasingCase: LeasingCaseType) {
  const baseUrl = window.location.origin;
  return `${baseUrl}/leasing-dmf/${getCaseId(leasingCase)}`;
}

function getBusinessInfoBlock(leasingCase: LeasingCaseType) {
  if (leasingCase.customer?.typeId !== 2) return "";

  return `
Til brug for jeres administration fremgår oplysninger om momsfradrag og beskatningsgrundlag nedenfor:
• Månedligt momsfradrag: ${getMonthlyVatDeduction(leasingCase)}
• Beskatningsgrundlag: ${getTaxation(leasingCase)}
`;
}

function getInsuranceBlock(leasingCase: LeasingCaseType) {
  const insuranceCompanyName = getInsuranceCompanyName(leasingCase).toLowerCase();
  if (insuranceCompanyName.includes("købstædernes")) {
    return `
Forsikring
Du er tilmeldt vores flådeforsikring hos Købstædernes Forsikring og vil modtage en separat mail med forsikringscertifikatet til bilen.
På certifikatet finder du de relevante oplysninger om forsikringen samt information om, hvad du skal gøre, hvis uheldet er ude.
`;
  }

  if (insuranceCompanyName.includes("tryg")) {
    return `
Forsikring
Du er tilmeldt vores flådeforsikring hos Tryg Forsikring. Forsikringscertifikatet til bilen er vedhæftet denne mail.
På certifikatet finder du de relevante oplysninger om forsikringen samt information om, hvad du skal gøre, hvis uheldet er ude.
`;
  }

  return `
I tilfælde af forsikringsskift i løbet af leasingperioden, kan leasingtagers nye forsikringsselskab opkræve leasinggiver et gebyr op til kr. 1250,- for ny panthaverdeklaration.
Såfremt denne opkrævning forekommer, vil dette blive viderefaktureret til leasingtager + administrationsgebyr på kr. 400,- + moms.
`;
}
